import { useAuth } from '@ox-technologies/auth';
import { PageLoading } from '@ox-technologies/ui';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { CUSTOM_AUTH_TOKEN } from 'config';
import { lazy, ReactNode, Suspense } from 'react';
import Layout from 'utils/layout/layout';
import ProtectedRoutesLayout from 'utils/layout/protected-routes-layout';
import PublicRoutesLayout from 'utils/layout/public-routes-layout';
import { ProtectedRoute } from 'utils/routing/protected-route';
import { appRoutes } from 'utils/routing/routes';
import { z } from 'zod';

const ChangeForgottenPassword = lazy(
  () => import('pages/change-forgotten-password/change-forgotten-password')
);
const ChangePassword = lazy(
  () => import('pages/change-password/change-password')
);
const Dashboard = lazy(() => import('pages/dashboard/dashboard'));
const DeviceCode = lazy(() => import('pages/device-code/device-code'));
const Error = lazy(() => import('pages/error/error'));
const ForgottenPasswordPage = lazy(
  () => import('pages/forgotten-password/forgotten-password')
);
const Homepage = lazy(() => import('pages/homepage/homepage'));
const LoggedOut = lazy(() => import('pages/logged-out/logged-out'));
const Login = lazy(() => import('pages/login/login'));
const Logout = lazy(() => import('pages/logout/logout'));
const NotFound = lazy(() => import('pages/not-found/not-found'));
const Profile = lazy(() => import('pages/profile/profile'));

const RegisterByEmailInvitation = lazy(
  () =>
    import('pages/register-by-email-invitation/register-by-email-invitation')
);

const useIsSignedIn = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated || !!CUSTOM_AUTH_TOKEN;
};

const Page = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
);

// Pathless layout route component for protected pages: enforces auth
// (unauthenticated users are sent to the homepage) and renders the signed-in
// app chrome (navbar/footer) around its children.
const ProtectedLayout = () => {
  const isSignedIn = useIsSignedIn();

  if (!isSignedIn) return <Navigate to={appRoutes.home} />;

  return (
    <ProtectedRoutesLayout>
      <Outlet />
    </ProtectedRoutesLayout>
  );
};

// Pathless layout route component for public pages: the LOX-branded shell
// (welcome headings + login illustration) around its children. Chosen by
// route, so it shows even when a dev CUSTOM_AUTH_TOKEN is set.
const PublicLayout = () => (
  <PublicRoutesLayout>
    <Outlet />
  </PublicRoutesLayout>
);

// Inverse protection: forbid signed-in users from accessing some public
// routes (they are redirected to `navigateTo` instead).
const PublicOnlyPage = ({
  navigateTo,
  children,
}: {
  navigateTo: string;
  children: ReactNode;
}) => {
  const isSignedIn = useIsSignedIn();

  return (
    <ProtectedRoute canAccessThisRoute={!isSignedIn} navigateTo={navigateTo}>
      <Page>{children}</Page>
    </ProtectedRoute>
  );
};

// Search params are tolerant on purpose: every param is optional, coerced to
// a string and never throws (`catch`), and unknown params pass through — the
// old react-router code read search params via URLSearchParams and tolerated
// anything.
const optionalString = z.coerce.string().optional().catch(undefined);

const rootSearchSchema = z
  .object({
    // OIDC login redirect (see useLoginUrlParams / login page)
    RedirectUrl: optionalString,
    // logout page post-logout redirect target
    postLogoutRedirectUri: optionalString,
    // device-code page verification code
    user_code: optionalString,
    // profile page e-mail verification key
    key: optionalString,
    // register-by-email-invitation redirect target
    redirectUrl: optionalString,
  })
  .passthrough();

type RootSearchParams = {
  RedirectUrl?: string;
  postLogoutRedirectUri?: string;
  user_code?: string;
  key?: string;
  redirectUrl?: string;
};

// Root route — validates shared search params used across the entire app.
// The explicit return type keeps `useSearch` typing clean while the
// `.passthrough()` schema preserves unknown params at runtime.
const rootRoute = createRootRoute({
  component: Layout,
  notFoundComponent: () => (
    <PublicRoutesLayout>
      <Page>
        <NotFound />
      </Page>
    </PublicRoutesLayout>
  ),
  validateSearch: (search): RootSearchParams => rootSearchSchema.parse(search),
});

// Pathless layout route that wraps all public pages in the branded shell
const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: PublicLayout,
});

// Layout route that enforces authentication for all its children
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
});

const homeRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.home,
  component: () => (
    <Page>
      <Homepage />
    </Page>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.logIn,
  component: () => (
    <Page>
      <Login />
    </Page>
  ),
});

const logoutRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.logOut,
  component: () => (
    <Page>
      <Logout />
    </Page>
  ),
});

const errorRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.error,
  component: () => (
    <Page>
      <Error />
    </Page>
  ),
});

const registerByEmailInvitationRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.registerByEmailInvitation,
  component: () => (
    <PublicOnlyPage navigateTo={appRoutes.registerByEmailInvitation}>
      <RegisterByEmailInvitation />
    </PublicOnlyPage>
  ),
});

const forgottenPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.forgottenPassword,
  component: () => (
    <PublicOnlyPage navigateTo={appRoutes.dashboard}>
      <ForgottenPasswordPage />
    </PublicOnlyPage>
  ),
});

const changeForgottenPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.changeForgottenPassword,
  component: () => (
    <Page>
      <ChangeForgottenPassword />
    </Page>
  ),
});

const loggedOutRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: appRoutes.loggedOut,
  component: () => (
    <PublicOnlyPage navigateTo={appRoutes.dashboard}>
      <LoggedOut />
    </PublicOnlyPage>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: appRoutes.dashboard,
  component: () => (
    <Page>
      <Dashboard />
    </Page>
  ),
});

const changePasswordRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: appRoutes.changePassword,
  component: () => (
    <Page>
      <ChangePassword />
    </Page>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: appRoutes.profile,
  component: () => (
    <Page>
      <Profile />
    </Page>
  ),
});

const deviceCodeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: appRoutes.deviceCode,
  component: () => (
    <Page>
      <DeviceCode />
    </Page>
  ),
});

const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([
    homeRoute,
    loginRoute,
    logoutRoute,
    errorRoute,
    registerByEmailInvitationRoute,
    forgottenPasswordRoute,
    changeForgottenPasswordRoute,
    loggedOutRoute,
  ]),
  protectedRoute.addChildren([
    dashboardRoute,
    changePasswordRoute,
    profileRoute,
    deviceCodeRoute,
  ]),
]);

// Mirror URLSearchParams semantics of react-router: search param values are
// always plain strings and are never JSON round-tripped (TanStack's default
// serializer would rewrite `?user_code=123456` as `?user_code=%22123456%22`).
// Like URLSearchParams.get(), the first occurrence of a repeated key wins.
const parseSearch = (searchStr: string): Record<string, string> => {
  const params = new URLSearchParams(searchStr);
  const result: Record<string, string> = {};
  for (const [paramKey, value] of params.entries()) {
    if (!(paramKey in result)) result[paramKey] = value;
  }
  return result;
};

const stringifySearch = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([paramKey, value]) => {
    if (value !== undefined) params.set(paramKey, String(value));
  });
  const str = params.toString();
  return str ? `?${str}` : '';
};

export const router = createRouter({ routeTree, parseSearch, stringifySearch });

// Register for full type-safety across the app
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
